import {
    inject,
    Injectable
} from '@angular/core';

import { restManager } from 'app/ajs-upgraded-providers';

import {
    ConfigurationManagerService,
    ConnectionService,
    EntityManagerService
} from 'src/app/core';

import { upperFirst } from 'src/app/shared/string-utils';

import {
    Customers,
    SalesCustomer
} from 'tilby-models';

type VatInfoResponse = {
    type: string;
    company_name?: string;
    country?: string;
    billing_street?: string;
    billing_number?: string;
    billing_city?: string;
    billing_prov?: string;
    billing_zip?: string;
    billing_country?: string;
    email_pec?: string;
    tax_code?: string;
    vat_code?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CustomerUtilsService {
    private readonly restManager = inject(restManager);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly connection = inject(ConnectionService);
    private readonly entityManager = inject(EntityManagerService);

    /**
     * Whether the "customers.verify_vat_tax_code" preference is enabled.
     */
    public isVatTaxCodeCheckEnabled() {
        return !!this.configurationManagerService.getPreference('customers.verify_vat_tax_code');
    }

    /**
     * Looks for an existing customer with the same VAT code / tax code of the given one.
     * Matching rules:
     *  - only VAT code filled: same VAT code and no tax code
     *  - only tax code filled: same tax code and no VAT code
     *  - both filled: both equal
     * The customer identified by `id` is ignored.
     * When `original` is given (edit), the check runs only if VAT code or tax code differ from it.
     *
     * The search is done online. With `allowOffline` (offline-first contexts, e.g. the sale dialogs) it falls back to the
     * local customers when offline or when the online request fails; otherwise the error is thrown so the caller can block the save.
     *
     * @returns the duplicate customer, or `null` if there is none (or nothing to check).
     */
    public async findDuplicateByVatTaxCode(
        customer: { id?: number | null; uuid?: string | null; vat_code?: string | null; tax_code?: string | null },
        original?: { vat_code?: string | null; tax_code?: string | null } | null,
        options?: { allowOffline?: boolean }
    ) {
        const normalize = (value?: string | null) => (value ?? '').trim().toUpperCase();
        const vat = normalize(customer.vat_code);
        const tax = normalize(customer.tax_code);

        if(!vat && !tax) {
            return null;
        }

        if(original && normalize(original.vat_code) === vat && normalize(original.tax_code) === tax) {
            return null;
        }

        let results: Customers[];

        if(options?.allowOffline && this.connection.isOffline()) {
            results = await this.entityManager.customers.fetchCollectionOffline();
        } else {
            try {
                const query = vat ? { vat_code: customer.vat_code!.trim() } : { tax_code: customer.tax_code!.trim() };
                results = await this.entityManager.customers.fetchCollectionOnline({ ...query, pagination: false }) as Customers[];
            } catch (e) {
                if(!options?.allowOffline) {
                    throw e;
                }

                results = await this.entityManager.customers.fetchCollectionOffline();
            }
        }

        return results.find((c) => {
            if((customer.id != null && c.id === customer.id) || (customer.uuid && c.uuid === customer.uuid)) {
                return false;
            }

            return normalize(c.vat_code) === vat && normalize(c.tax_code) === tax;
        }) ?? null;
    }

    /**
     * Retrieves VAT information for a given VAT code.
     * This function uses the `restManager` to fetch VAT data and constructs a
     * `VatInfoResponse` object, filtering out any fields with empty values.
     * 
     * @param vatCode The VAT code to validate and retrieve information for.
     * @returns A promise that resolves to a `VatInfoResponse` object containing
     *          company and address details if found, or `null` if not found.
     * 
     */
    public async getVatInfo(vatCode: string) {
        const result = await this.restManager.getOne('validate_vat', vatCode);

        if(!result) {
            return null;
        }

        const customer = {
            type: 'company',
            company_name: result.name,
            country: result.address_country?.slice(0, 2),
            billing_street: result.address_street,
            billing_number: result.address_number?.slice(0, 25),
            billing_city: result.address_city,
            billing_prov: result.address_prov?.slice(0, 10),
            billing_zip: result.address_zip?.slice(0, 10),
            billing_country: result.address_country?.slice(0, 2),
            email_pec: result.email_pec?.toLowerCase(),
            tax_code: result.tax_number?.slice(0, 16),
            vat_code: result.vat_number,
        };

        // remove empty values
        return Object.fromEntries(Object.entries(customer).filter(([key, value]) => value)) as VatInfoResponse;
    }

    /**
     * Returns the customer name as a string, given a customer object.
     * The customer name is constructed as follows:
     *  - If the customer has a company name, that is used.
     *  - If the customer has first and last names, they are used, with the first
     *    name capitalized.
     *  - If the customer has neither a company name nor first and last names,
     *    null is returned.
     *
     * @param customer The customer object.
     * @returns The customer name as a string, or null.
     */
    public static getCustomerName(customer?: Customers | SalesCustomer) {
        if (customer?.company_name) {
            return customer.company_name;
        } else if (customer?.first_name && customer?.last_name) {
            return `${upperFirst(customer.first_name)} ${upperFirst(customer.last_name)}`;
        }

        return null;
    }
}